import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "~/services/api-client";

interface TimeseriesDataPoint {
  datetime: string;
  price: number;
  percent_move: number;
}

interface DailyTimeseriesData {
  figi: string;
  price: number;
  previous_close: number;
  percent_move: number;
  open: number;
  close: number;
  volume: number;
  timeseries: TimeseriesDataPoint[];
}

interface UseSharedDailyTimeseriesProps {
  figi?: string;
  autoFetch?: boolean;
}

interface UseSharedDailyTimeseriesReturn {
  data: DailyTimeseriesData | null;
  isLoading: boolean;
  error: string | null;
  fetchData: () => Promise<void>;
}

// Shared cache for daily timeseries data
const dailyTimeseriesCache = new Map<
  string,
  { data: DailyTimeseriesData; timestamp: number }
>();
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

export function useSharedDailyTimeseries({
  figi,
  autoFetch = false,
}: UseSharedDailyTimeseriesProps = {}): UseSharedDailyTimeseriesReturn {
  const [data, setData] = useState<DailyTimeseriesData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async (forceFresh = false, targetFigi?: string) => {
      const useFigi = targetFigi || figi;

      if (!useFigi) {
        setError("No FIGI provided");
        return;
      }

      // Abort any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Clear cache if forcing fresh data
      if (forceFresh) {
        dailyTimeseriesCache.delete(useFigi);
      }

      // Check cache first
      const cached = dailyTimeseriesCache.get(useFigi);
      if (
        cached &&
        Date.now() - cached.timestamp < CACHE_DURATION &&
        !forceFresh
      ) {
        console.log("Using cached daily timeseries data for:", useFigi);
        setData(cached.data);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          figi: useFigi,
          interval: "DAY",
        });

        console.log("Fetching daily timeseries for KeyStats:", useFigi);
        const response = await apiClient.request({
          method: "GET",
          url: `/api/v1/timeseries/securities?${params}`,
          signal: abortControllerRef.current.signal,
        });

        if (response.error) {
          setError(response.error);
          return;
        }

        if (response.data && response.data.length > 0) {
          const timeseriesData = response.data[0];
          console.log("Daily timeseries data fetched for FIGI:", useFigi);

          // Cache the data
          dailyTimeseriesCache.set(useFigi, {
            data: timeseriesData,
            timestamp: Date.now(),
          });

          setData(timeseriesData);
        } else {
          setError("No daily timeseries data available");
        }
      } catch (err) {
        // Don't show error for intentionally aborted requests
        if ((err as Error)?.name === "AbortError") {
          console.log("Daily timeseries fetch aborted for:", useFigi);
          return;
        }
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error occurred";
        console.error("Daily timeseries fetch error:", err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [figi]
  );

  // Clear data when FIGI changes to prevent stale data
  useEffect(() => {
    setData(null);
    setError(null);
  }, [figi]);

  useEffect(() => {
    if (autoFetch && figi) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [figi, autoFetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    data,
    isLoading,
    error,
    fetchData,
  };
}
